import React, { useMemo } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
// import { Highlight, type Language, themes } from 'prism-react-renderer';
import Fonts from '../../../../../ui/global/Fonts';
import Colors from '../../../../../ui/global/Colors';
import normalizeContent from '../helpers/normalizeContent';
import hideIncompleteMarkdown from '../helpers/hideIncompleteMarkdown';

interface MarkdownProps {
  content: string;
  className?: string;
  inline?: boolean;
  maxImageWidth?: number | string;
  maxImageHeight?: number | string;
}

const NORMALIZE_CONFIG: {
  fileBullets: boolean;
  hideIncompleteMarkdown: boolean;
} = {
  fileBullets: true,
  hideIncompleteMarkdown: true,
};

// Build a strict sanitize schema. No raw HTML execution.
const useSanitizeSchema = () => {
  return useMemo(() => {
    const extended = { ...defaultSchema } as any;
    extended.tagNames = Array.from(new Set([
      ...(defaultSchema.tagNames || []),
      'h1', 'h2', 'h3', 'h4', 'p', 'blockquote', 'hr',
      'ul', 'ol', 'li', 'strong', 'em', 'code', 'pre', 'a', 'img'
    ]));
    extended.attributes = {
      ...(defaultSchema.attributes || {}),
      a: [
        ...(((defaultSchema.attributes || {}) as any).a || []),
        ['a', 'href'],
        ['a', 'title'],
        ['a', 'rel'],
        ['a', 'target']
      ],
      img: [
        ...(((defaultSchema.attributes || {}) as any).img || []),
        ['img', 'src'],
        ['img', 'alt'],
        ['img', 'title'],
        ['img', 'width'],
        ['img', 'height']
      ],
      code: [
        ...(((defaultSchema.attributes || {}) as any).code || []),
        ['code', 'className'] // language-*
      ],
      pre: [
        ...(((defaultSchema.attributes || {}) as any).pre || []),
        ['pre', 'className']
      ]
    };
    return extended;
  }, []);
};

const isAllowedHref = (href?: string) => {
  if (!href) return false;
  const allowed = ['http:', 'https:'];
  try {
    const url = new URL(href, window.location.origin);
    return allowed.includes(url.protocol);
  } catch (_e) {
    return false;
  }
};


const Markdown: React.FC<MarkdownProps> = ({
  content,
  className,
  inline = false,
  maxImageWidth = '100%',
  maxImageHeight = 240,
}) => {
  const sanitizeSchema = useSanitizeSchema();

  const components = useMemo(() => ({
    // Display code as plain text without syntax highlighting
    code({ inline: isInline, className: codeClassName, children, ...props }: any) {
      const raw = String(children || '');
      // const match = /language-(\w+)/.exec(codeClassName || '');
      // const language = (match && match[1] ? match[1] : 'tsx') as Language;

      if (isInline) {
        return (
          <InlineCode {...props}>
            {raw}
          </InlineCode>
        );
      }

      // If this is a single-line, language-less block that looks like a filename,
      // render it inline to avoid bulky <pre> inside list items.
      const trimmed = raw.trim();
      const looksLikeFilename = /\.[a-zA-Z0-9]{1,6}$/.test(trimmed) && !/\n/.test(trimmed) && (!codeClassName || !/language-/.test(codeClassName));
      if (looksLikeFilename && trimmed.length <= 80) {
        return (
          <InlineCode {...props}>
            {trimmed}
          </InlineCode>
        );
      }

      // Display block code inline (not starting on a new line)
      return (
        <PlainCodeInline>
          {raw}
        </PlainCodeInline>
      );

      // Code with syntax highlight
      // return (
      //   <CodeBlock>
      //     <Highlight theme={themes.vsDark} code={raw.replace(/\n$/, '')} language={language}>
      //       {({ className: cls, style, tokens, getLineProps, getTokenProps }) => (
      //         <pre className={cls} style={style}>
      //           {tokens.map((line, i) => (
      //             <div key={i} {...getLineProps({ line, key: i })}>
      //               {line.map((token, key) => (
      //                 <span key={key} {...getTokenProps({ token, key })} />
      //               ))}
      //             </div>
      //           ))}
      //         </pre>
      //       )}
      //     </Highlight>
      //   </CodeBlock>
      // );
    },
    a({ href, children, ...props }: any) {
      const safe = isAllowedHref(href);
      if (!safe) {
        return <span {...props}>{children}</span>;
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
    img({ src, alt, title }: any) {
      const safe = isAllowedHref(src);
      if (!safe) return null;
      return (
        <MarkdownImage
          src={src}
          alt={alt || ''}
          title={title}
          style={{ maxWidth: typeof maxImageWidth === 'number' ? `${maxImageWidth}px` : maxImageWidth, maxHeight: typeof maxImageHeight === 'number' ? `${maxImageHeight}px` : maxImageHeight }}
        />
      );
    },
  }), [maxImageHeight, maxImageWidth]);

  const allowedElements = inline ? ['em', 'strong', 'code', 'a', 'span', 'text'] : undefined;

  const remarkPluginsArr = useMemo(() => {
    const arr: any[] = [remarkGfm];
    if (!inline && NORMALIZE_CONFIG.fileBullets) arr.unshift(normalizeContent as any);
    return arr;
  }, [inline]);

  return (
    <MarkdownRoot className={className} $inline={inline}>
      <ReactMarkdown
        remarkPlugins={remarkPluginsArr as any}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
        components={components as any}
        allowedElements={allowedElements as any}
      >
        {NORMALIZE_CONFIG.hideIncompleteMarkdown ? hideIncompleteMarkdown(content || '') : (content || '')}
      </ReactMarkdown>
    </MarkdownRoot>
  );
};

export default Markdown;

const MarkdownRoot = styled.div<{ $inline: boolean }>`
  /* Base typography aligned with stream */
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 14px;
  line-height: 1.3em;
  color: ${Colors.grey2};

  /* Ensure markdown layout isn't impacted by parent pre-wrap */
  white-space: normal; // pre-wrap, pre-line
  word-break: break-word;

  & p {
    margin: 8px 0;
  }

  & h1, & h2, & h3, & h4, & h5, & h6 {
    color: ${Colors.grey2};
    margin: 10px 0 6px 0;
    line-height: 1.2em;
  }

  & ul { margin: 6px 0; padding-left: 20px; list-style: disc; }
  & ol { margin: 6px 0; padding-left: 20px; list-style: decimal; }

  & li {
    margin: 3px 0;
    line-height: 1.3em;
    font-size: 13px;
  }

  /* Remove extra paragraph spacing inside list items */
  & li > p {
    margin: 0;
    line-height: 1.3em;
  }

  /* Bold text */
  & strong, & b {
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
  }

  & blockquote {
    margin: 8px 0;
    padding-left: 10px;
    border-left: 3px solid rgba(255, 255, 255, 0.15);
    color: ${Colors.grey2};
  }

  & hr {
    border: none;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 8px 0;
  }
`;

const InlineCode = styled.code`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  background: transparent;
  color: inherit;
  padding: 0;
  margin: 0;
  border-radius: 0;
  display: inline;
`;

const CodeBlock = styled.div`
  & pre {
    margin: 8px 0;
    padding: 10px 12px;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 11px;
    font-weight: 100;
    line-height: 1.3em;
    white-space: pre-wrap;
    word-break: break-word;
    max-width: 100%;
  }
`;

const PlainCodeInline = styled.code`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: inherit;
  line-height: inherit;
  color: ${Colors.grey2};
  padding: 0;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
  display: inline;
`;

const MarkdownImage = styled.img`
  display: block;
  margin: 6px 0;
  object-fit: contain;
  border-radius: 6px;
`;


